import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Sparkles,
  Play,
  Sigma,
  Power,
  ArrowLeft,
} from 'lucide-react';
import { leadScoringRulesAPI, leadsAPI } from '../utils/api';
import {
  LeadScoreRuleCreate,
  LeadScoreRuleResponse,
  ScoreOperator,
  SCORE_OPERATORS,
  SCORE_OPERATOR_LABEL,
  SCORE_RULE_FIELDS,
} from '../types/leadScoreRule';
import ConfirmModal from '../components/ConfirmModal';
import { usePermissions } from '../hooks/usePermissions';

type GroupKind = 'all' | 'any';

interface ConditionRow {
  field: string;
  op: ScoreOperator;
  valueRaw: string; // comma-separated for 'in'/'not_in', or csv of types for activity_count
  days?: number;
  types?: string; // csv for activity_count_last_days.types
}

interface RuleEditState {
  id?: string;
  name: string;
  description: string;
  points: number;
  is_active: boolean;
  priority: number;
  groupKind: GroupKind;
  conditions: ConditionRow[];
}

const emptyRow: ConditionRow = {
  field: 'stato',
  op: 'eq',
  valueRaw: '',
  days: undefined,
  types: '',
};

const emptyRule: RuleEditState = {
  name: '',
  description: '',
  points: 10,
  is_active: true,
  priority: 0,
  groupKind: 'all',
  conditions: [],
};

function parseValueForOp(raw: string, op: ScoreOperator): unknown {
  if (op === 'in' || op === 'not_in') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (op === 'exists') return true;
  // Heuristic: number if starts with digit or -digit
  const trimmed = raw.trim();
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  return trimmed;
}

function stringifyValue(v: unknown): string {
  if (Array.isArray(v)) return v.map(String).join(', ');
  if (v === null || v === undefined) return '';
  return String(v);
}

function deserializeRule(rule: LeadScoreRuleResponse): RuleEditState {
  const conds = rule.conditions || {};
  let groupKind: GroupKind = 'all';
  let rows: any[] = [];
  if (Array.isArray((conds as any).all)) {
    groupKind = 'all';
    rows = (conds as any).all;
  } else if (Array.isArray((conds as any).any)) {
    groupKind = 'any';
    rows = (conds as any).any;
  }
  return {
    id: rule.id,
    name: rule.name,
    description: rule.description || '',
    points: rule.points,
    is_active: rule.is_active,
    priority: rule.priority,
    groupKind,
    conditions: rows.map((r) => ({
      field: r.field || 'stato',
      op: (r.op as ScoreOperator) || 'eq',
      valueRaw: stringifyValue(r.value),
      days: r.days,
      types: Array.isArray(r.types) ? r.types.join(', ') : r.types || '',
    })),
  };
}

function serializeRule(state: RuleEditState): LeadScoreRuleCreate {
  const leaves = state.conditions
    .map((c) => {
      const node: Record<string, unknown> = { field: c.field, op: c.op };
      if (c.op !== 'exists') {
        node.value = parseValueForOp(c.valueRaw, c.op);
      }
      if (c.field === 'activity_count_last_days') {
        if (c.days) node.days = Number(c.days);
        const types = (c.types || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (types.length) node.types = types;
      }
      return node;
    })
    .filter((n) => !!n.field);

  const conditions: Record<string, unknown> = leaves.length
    ? { [state.groupKind]: leaves }
    : {};

  return {
    name: state.name,
    description: state.description || undefined,
    points: state.points,
    is_active: state.is_active,
    priority: state.priority,
    conditions,
  };
}

export default function LeadScoringRules() {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('leads:config:edit');
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState<RuleEditState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeadScoreRuleResponse | null>(null);
  const [recomputeAllOpen, setRecomputeAllOpen] = useState(false);
  const [testLeadSearch, setTestLeadSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['scoring-rules', page],
    queryFn: async () => {
      const res = await leadScoringRulesAPI.getRules({ page, limit: 30 });
      return res.data;
    },
  });

  const rules = data?.data || [];
  const pagination = data?.pagination;

  const saveMutation = useMutation({
    mutationFn: (state: RuleEditState) => {
      const payload = serializeRule(state);
      if (state.id) return leadScoringRulesAPI.updateRule(state.id, payload);
      return leadScoringRulesAPI.createRule(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoring-rules'] });
      toast.success(edit?.id ? 'Regola aggiornata' : 'Regola creata');
      setEdit(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadScoringRulesAPI.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoring-rules'] });
      toast.success('Regola eliminata');
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => leadScoringRulesAPI.toggleRule(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['scoring-rules'] });
      toast.success(res.data.is_active ? 'Regola attivata' : 'Regola disattivata');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel toggle');
    },
  });

  const recomputeAllMutation = useMutation({
    mutationFn: () => leadScoringRulesAPI.recomputeAll(),
    onSuccess: (res) => {
      setRecomputeAllOpen(false);
      toast.success(
        `Rivalutazione completata: ${res.data.changed}/${res.data.total} lead aggiornati`
      );
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella rivalutazione');
    },
  });

  if (edit) {
    return (
      <RuleEditor
        state={edit}
        onChange={setEdit}
        onSave={() => saveMutation.mutate(edit)}
        onCancel={() => setEdit(null)}
        isSaving={saveMutation.isPending}
        testLeadSearch={testLeadSearch}
        setTestLeadSearch={setTestLeadSearch}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Regole di scoring</h1>
          <p className="text-gray-600">
            Punteggi automatici assegnati ai lead in base a condizioni
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button
              onClick={() => setRecomputeAllOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 text-sm"
            >
              <Sigma className="h-4 w-4" />
              Rivaluta tutti i lead
            </button>
            <button
              onClick={() => setEdit({ ...emptyRule })}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
              Nuova regola
            </button>
          </div>
        )}
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        <strong>Come funziona:</strong> ogni regola ha un gruppo di condizioni (AND o OR) e un
        punteggio (da −100 a +100). Il sistema somma i punti delle regole che matchano un lead
        e ritaglia il risultato a [0, 100]. Lo scoring è ricalcolato automaticamente quando un
        lead cambia stato o riceve una nuova attività.
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessuna regola</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Punti
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Condizioni
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rules.map((r) => {
                const count = countLeaves(r.conditions);
                const groupKind = (r.conditions as any)?.all
                  ? 'all'
                  : (r.conditions as any)?.any
                    ? 'any'
                    : 'none';
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{r.name}</div>
                      {r.description && (
                        <div className="text-xs text-gray-500 truncate max-w-md">
                          {r.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-sm font-bold ${
                          r.points >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {r.points > 0 ? `+${r.points}` : r.points}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{r.priority}</td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {groupKind === 'none' ? (
                        <span className="italic">match sempre</span>
                      ) : (
                        <span>
                          <strong>{groupKind.toUpperCase()}</strong> · {count} leaf
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {canEdit ? (
                        <button
                          onClick={() => toggleMutation.mutate(r.id)}
                          disabled={toggleMutation.isPending}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                            r.is_active
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          <Power className="h-3 w-3" />
                          {r.is_active ? 'Attiva' : 'Inattiva'}
                        </button>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            r.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          <Power className="h-3 w-3" />
                          {r.is_active ? 'Attiva' : 'Inattiva'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {canEdit && (
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => setEdit(deserializeRule(r))}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                            title="Modifica"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(r)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                            title="Elimina"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} regole)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {deleteTarget && (
        <ConfirmModal
          isOpen={!!deleteTarget}
          title="Elimina regola"
          message={`Eliminare la regola "${deleteTarget.name}"? I punteggi esistenti restano, ma la regola non sarà più applicata nelle future rivalutazioni.`}
          confirmLabel="Elimina"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onClose={() => setDeleteTarget(null)}
          isLoading={deleteMutation.isPending}
          variant="danger"
        />
      )}

      {recomputeAllOpen && (
        <ConfirmModal
          isOpen={recomputeAllOpen}
          title="Rivaluta tutti i lead"
          message="Verranno ricalcolati gli score di TUTTI i lead del tenant applicando le regole attive. Per tenant con molti lead l'operazione può essere lenta. Procedere?"
          confirmLabel="Rivaluta"
          onConfirm={() => recomputeAllMutation.mutate()}
          onClose={() => setRecomputeAllOpen(false)}
          isLoading={recomputeAllMutation.isPending}
          variant="warning"
        />
      )}
    </div>
  );
}

function countLeaves(conditions: Record<string, unknown>): number {
  if (!conditions) return 0;
  const group = (conditions as any).all || (conditions as any).any;
  return Array.isArray(group) ? group.length : 0;
}

interface EditorProps {
  state: RuleEditState;
  onChange: (next: RuleEditState) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  testLeadSearch: string;
  setTestLeadSearch: (v: string) => void;
}

function RuleEditor({
  state,
  onChange,
  onSave,
  onCancel,
  isSaving,
  testLeadSearch,
  setTestLeadSearch,
}: EditorProps) {
  const previewJSON = useMemo(() => {
    const payload = serializeRule(state);
    return JSON.stringify(payload.conditions, null, 2);
  }, [state]);

  const setField = <K extends keyof RuleEditState>(k: K, v: RuleEditState[K]) =>
    onChange({ ...state, [k]: v });

  const updateRow = (idx: number, patch: Partial<ConditionRow>) => {
    const next = [...state.conditions];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...state, conditions: next });
  };

  const addRow = () =>
    onChange({ ...state, conditions: [...state.conditions, { ...emptyRow }] });

  const removeRow = (idx: number) => {
    const next = state.conditions.filter((_, i) => i !== idx);
    onChange({ ...state, conditions: next });
  };

  // Test on a lead
  const [testResult, setTestResult] = useState<{
    leadId: string;
    old: number;
    next: number;
    delta: number;
  } | null>(null);

  const { data: leadsFound } = useQuery({
    queryKey: ['leads-test-search', testLeadSearch],
    queryFn: async () => {
      const res = await leadsAPI.getLeads({ limit: 8, search: testLeadSearch });
      return res.data.data;
    },
    enabled: testLeadSearch.length >= 2,
  });

  const runTestMutation = useMutation({
    mutationFn: (leadId: string) => leadScoringRulesAPI.recomputeOne(leadId),
    onSuccess: (res) => {
      setTestResult({
        leadId: res.data.lead_id,
        old: res.data.old_score,
        next: res.data.new_score,
        delta: res.data.delta,
      });
      toast.success(
        `Score ricalcolato: ${res.data.old_score} → ${res.data.new_score} (Δ ${res.data.delta > 0 ? '+' : ''}${res.data.delta})`
      );
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel test');
    },
  });

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Regole
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {state.id ? 'Modifica regola' : 'Nuova regola'}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Dati principali</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nome *</label>
                <input
                  type="text"
                  value={state.name}
                  onChange={(e) => setField('name', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Punti (−100..100) *</label>
                <input
                  type="number"
                  min={-100}
                  max={100}
                  value={state.points}
                  onChange={(e) => setField('points', Number(e.target.value))}
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Descrizione</label>
              <textarea
                rows={2}
                value={state.description}
                onChange={(e) => setField('description', e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Priority</label>
                <input
                  type="number"
                  value={state.priority}
                  onChange={(e) => setField('priority', Number(e.target.value))}
                  className={inputClass}
                />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={state.is_active}
                    onChange={(e) => setField('is_active', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Regola attiva
                </label>
              </div>
            </div>
          </div>

          {/* Conditions builder */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Condizioni</h3>
              <button
                type="button"
                onClick={addRow}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                Aggiungi condizione
              </button>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-700">La regola matcha quando</span>
              <select
                value={state.groupKind}
                onChange={(e) => setField('groupKind', e.target.value as GroupKind)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">TUTTE le condizioni (AND)</option>
                <option value="any">ALMENO UNA condizione (OR)</option>
              </select>
              <span className="text-sm text-gray-700">sono vere</span>
            </div>

            {state.conditions.length === 0 ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                Nessuna condizione: la regola matcherà sempre tutti i lead.
              </div>
            ) : (
              <ul className="space-y-2">
                {state.conditions.map((c, idx) => {
                  const fieldDef = SCORE_RULE_FIELDS.find((f) => f.value === c.field);
                  const needsDaysTypes = fieldDef?.hasDaysTypes;
                  const needsValue = c.op !== 'exists';
                  return (
                    <li
                      key={idx}
                      className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                        <div className="md:col-span-4">
                          <label className="block text-xs text-gray-500 mb-0.5">Campo</label>
                          <select
                            value={c.field}
                            onChange={(e) => updateRow(idx, { field: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            {SCORE_RULE_FIELDS.map((f) => (
                              <option key={f.value} value={f.value}>
                                {f.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-xs text-gray-500 mb-0.5">Operatore</label>
                          <select
                            value={c.op}
                            onChange={(e) => updateRow(idx, { op: e.target.value as ScoreOperator })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            {SCORE_OPERATORS.map((o) => (
                              <option key={o} value={o}>
                                {SCORE_OPERATOR_LABEL[o]}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-4">
                          <label className="block text-xs text-gray-500 mb-0.5">
                            Valore{' '}
                            {(c.op === 'in' || c.op === 'not_in') && (
                              <span className="text-gray-400">(separati da virgola)</span>
                            )}
                          </label>
                          <input
                            type="text"
                            disabled={!needsValue}
                            value={c.valueRaw}
                            onChange={(e) => updateRow(idx, { valueRaw: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder={c.op === 'exists' ? 'n/d' : '...'}
                          />
                        </div>
                        <div className="md:col-span-1 flex items-end">
                          <button
                            type="button"
                            onClick={() => removeRow(idx)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                            title="Rimuovi"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        {needsDaysTypes && (
                          <>
                            <div className="md:col-span-3">
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Giorni indietro
                              </label>
                              <input
                                type="number"
                                min={1}
                                value={c.days ?? ''}
                                onChange={(e) =>
                                  updateRow(idx, {
                                    days: e.target.value ? Number(e.target.value) : undefined,
                                  })
                                }
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="7"
                              />
                            </div>
                            <div className="md:col-span-9">
                              <label className="block text-xs text-gray-500 mb-0.5">
                                Tipi attività (csv, es. <code>call,email,meeting</code>)
                              </label>
                              <input
                                type="text"
                                value={c.types || ''}
                                onChange={(e) => updateRow(idx, { types: e.target.value })}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Test rule */}
          <div className="bg-white rounded-lg shadow p-6 space-y-3">
            <div className="flex items-center gap-2">
              <Play className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Test su un lead</h3>
            </div>
            <p className="text-xs text-gray-500">
              Ricalcola lo score del lead selezionato applicando TUTTE le regole attive (non
              solo questa). Utile per verificare se la regola si combina correttamente con le
              altre. Salva prima le modifiche a questa regola per includerle nel test.
            </p>
            <div className="relative">
              <input
                type="text"
                placeholder="Cerca un lead per nome, email, telefono..."
                value={testLeadSearch}
                onChange={(e) => setTestLeadSearch(e.target.value)}
                className={inputClass}
              />
              {leadsFound && leadsFound.length > 0 && testLeadSearch.length >= 2 && (
                <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                  {leadsFound.map((lead) => (
                    <li key={lead.id}>
                      <button
                        type="button"
                        onClick={() => {
                          runTestMutation.mutate(lead.id);
                          setTestLeadSearch('');
                        }}
                        disabled={runTestMutation.isPending}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className="font-medium">
                          {lead.denominazione_completa || '—'}
                        </span>
                        {lead.email && (
                          <span className="ml-2 text-xs text-gray-500">· {lead.email}</span>
                        )}
                        <span className="ml-2 text-xs text-orange-600 font-semibold">
                          score {lead.score}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {testResult && (
              <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
                Risultato: <strong>{testResult.old}</strong> →{' '}
                <strong>{testResult.next}</strong> (Δ{' '}
                <span
                  className={
                    testResult.delta >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'
                  }
                >
                  {testResult.delta > 0 ? `+${testResult.delta}` : testResult.delta}
                </span>
                )
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 text-sm"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving || !state.name.trim()}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {state.id ? 'Salva modifiche' : 'Crea regola'}
            </button>
          </div>
        </div>

        {/* RIGHT: JSON preview */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
              Anteprima condizioni (JSON)
            </h3>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto font-mono">
              {previewJSON || '{}'}
            </pre>
            <p className="text-xs text-gray-500">
              Questo è il payload <code>conditions</code> che verrà salvato sul backend.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 space-y-2 text-xs text-gray-600">
            <p className="font-semibold text-gray-900">Note operatori</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                <code>in</code> / <code>not_in</code>: valore = lista separata da virgole.
              </li>
              <li>
                <code>exists</code>: nessun valore richiesto (matcha se campo &ne; null).
              </li>
              <li>
                <code>contains</code>: per stringhe e array.
              </li>
              <li>
                Campo <code>activity_count_last_days</code> richiede <code>days</code> e
                opzionalmente <code>types</code> (csv).
              </li>
              <li>
                Campo <code>days_since_last_activity</code>: numero intero in giorni.
              </li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
}
